import { formatLogDate } from './action-log-format';
import { SOCIAL_ACTION_LABELS } from './constants';
import type { SocialActionDecision, SocialActionType } from './types';

/**
 * The vocabulary of the action log.
 *
 * Everything here is a description of what the engine already recorded — no value is invented and
 * none is derived by re-running a decision on the client. A log that disagrees with the log is
 * worse than no log.
 */

/**
 * Every decision the server's enum holds. Labels, pills and descriptions cover all of them,
 * because a row already written must still render.
 */
export const SOCIAL_ACTION_DECISIONS: SocialActionDecision[] = [
	'fired',
	'blocked',
	'queued',
	'lost_priority',
	'skipped',
	'no_match',
	'dry_run'
];

/**
 * What the filter offers — every decision the engine can still produce.
 *
 * `lost_priority` is not one of them. The audit log went to one row per COMMENT on 2026-08-20:
 * outranked rules are recorded on the winner's `precedence_losers` rather than as rows of their
 * own, and nothing writes this decision now. Offering it would be a filter that always returns
 * nothing on any account whose rows post-date the change.
 *
 * It stays in the enum above, and keeps its label and pill, because rows written before that date
 * still carry it and still have to read correctly. Retired is not the same as never happened.
 *
 * `skipped` is NOT retired — the pre-gate writes it whenever the kill switch, our own comment, an
 * already-actioned comment or the age cut-off stops a comment, and so does a failed delay handoff.
 */
export const FILTERABLE_DECISIONS: SocialActionDecision[] = SOCIAL_ACTION_DECISIONS.filter(
	(decision) => decision !== 'lost_priority'
);

export const DECISION_LABELS: Record<SocialActionDecision, string> = {
	fired: 'Fired',
	blocked: 'Blocked',
	queued: 'Queued',
	lost_priority: 'Lost priority',
	skipped: 'Skipped',
	no_match: 'No match',
	dry_run: 'Dry run'
};

/**
 * One sentence per decision, shown beside the pill when a row is open.
 *
 * "Why didn't my rule fire?" is the question this page exists to answer, and the decision name
 * alone does not answer it — `lost_priority` in particular reads as an error until you know it
 * means another rule won.
 */
export const DECISION_DESCRIPTIONS: Record<SocialActionDecision, string> = {
	fired: 'The action was taken.',
	blocked: 'A rule matched, but the risk gate or the verifier stopped it.',
	queued: 'Accepted and waiting out the account’s reply delay before it is sent.',
	lost_priority: 'This rule matched, but another rule had a lower priority number and ran instead.',
	skipped: 'The pre-gate skipped the comment — kill switch, our own comment, already actioned, or too old.',
	no_match: 'Nothing matched. Comments are terminal, so this means no action and no draft.',
	dry_run: 'Playground or dry run. Nothing was sent anywhere.'
};

/**
 * Pill colours, as design tokens rather than hex.
 *
 * Only four readings are worth distinguishing: something happened (green), something was stopped
 * (red), something is still owed (blue), and a rule was beaten (amber). The three decisions that
 * mean "the agent correctly did nothing" share one neutral pill — colouring them would imply a
 * problem where the answer is that the system behaved.
 */
export const DECISION_PILL_CLASSES: Record<SocialActionDecision, string> = {
	fired: 'bg-green-100 text-green-700',
	blocked: 'bg-red-100 text-red-700',
	queued: 'bg-blue-100 text-blue-700',
	lost_priority: 'bg-amber-100 text-amber-700',
	skipped: 'bg-muted text-muted-foreground',
	no_match: 'bg-muted text-muted-foreground',
	dry_run: 'bg-muted text-muted-foreground'
};

/**
 * The `reason` codes a dynamic reply's row can carry, exactly as the server writes them.
 *
 * `timedOut` is never stored: the log API answers it for a row that has waited on Kim too long.
 */
export const DYNAMIC_REPLY_REASONS = {
	awaiting: 'awaiting_dynamic_reply',
	notRequested: 'dynamic_reply_not_requested',
	failed: 'dynamic_reply_failed',
	belowThreshold: 'dynamic_reply_below_threshold',
	timedOut: 'dynamic_reply_timed_out'
} as const;

/** Every action the moderator can take, in the order the filter offers them. */
export const SOCIAL_LOG_ACTION_TYPES: SocialActionType[] = [
	'auto_close',
	'auto_flag',
	'auto_reply',
	'auto_hide',
	'auto_delete'
];

export const actionTypeLabel = (actionType: SocialActionType | null): string =>
	actionType ? SOCIAL_ACTION_LABELS[actionType] : '—';

// ── date window ───────────────────────────────────────────────────────────────

export type ActionLogRangePreset = 'last_24_hours' | 'last_7_days' | 'last_30_days' | 'last_90_days' | 'custom';

export interface ActionLogDateRange {
	preset: ActionLogRangePreset;
	/** ISO-8601. Only read when `preset` is `custom`; a relative preset is resolved against now. */
	from?: string;
	to?: string;
}

const HOURS_PER_DAY = 24;
const MILLISECONDS_PER_HOUR = 60 * 60 * 1000;

/**
 * The relative windows offered, in hours back from now.
 *
 * `custom` is absent on purpose: it has no duration, only endpoints the brand picked.
 */
const PRESET_HOURS: Record<Exclude<ActionLogRangePreset, 'custom'>, number> = {
	last_24_hours: HOURS_PER_DAY,
	last_7_days: 7 * HOURS_PER_DAY,
	last_30_days: 30 * HOURS_PER_DAY,
	last_90_days: 90 * HOURS_PER_DAY
};

export const RANGE_PRESET_LABELS: Record<ActionLogRangePreset, string> = {
	last_24_hours: 'Last 24 hours',
	last_7_days: 'Last 7 days',
	last_30_days: 'Last 30 days',
	last_90_days: 'Last 90 days',
	custom: 'Custom range'
};

/** The presets the picker lists. `custom` is reached by choosing dates, not by picking it. */
export const RANGE_PRESETS: Exclude<ActionLogRangePreset, 'custom'>[] = [
	'last_24_hours',
	'last_7_days',
	'last_30_days',
	'last_90_days'
];

/**
 * The default window.
 *
 * Seven days, which is also the server's default — so the page shows the same thing whether or not
 * the request carried a window, and the two can never quietly disagree.
 */
export const DEFAULT_ACTION_LOG_RANGE: ActionLogDateRange = { preset: 'last_7_days' };

/** What the closed date button says. A custom range names its endpoints; a preset names itself. */
export const describeRange = (range: ActionLogDateRange): string => {
	if (range.preset !== 'custom') return RANGE_PRESET_LABELS[range.preset];
	if (!range.from || !range.to) return RANGE_PRESET_LABELS.custom;
	return `${formatLogDate(range.from)} – ${formatLogDate(range.to)}`;
};

export interface ActionLogWindow {
	from: Date;
	to: Date;
}

/**
 * Turn a range into the two instants the request will carry.
 *
 * Resolved ONCE per filter change rather than per request: a relative preset resolved again at
 * export time would produce a slightly later `to` than the list was built with, and the CSV would
 * silently answer a different question from the screen it was launched from.
 */
export const resolveActionLogWindow = (range: ActionLogDateRange, now: Date = new Date()): ActionLogWindow => {
	if (range.preset === 'custom') {
		const from = range.from ? new Date(range.from) : null;
		const to = range.to ? new Date(range.to) : null;
		// A half-finished custom range is not an error to shout about — it is a picker mid-use.
		// Fall back to the default duration for whichever end is missing.
		const resolvedTo = to && !Number.isNaN(to.getTime()) ? to : now;
		const resolvedFrom =
			from && !Number.isNaN(from.getTime())
				? from
				: new Date(resolvedTo.getTime() - PRESET_HOURS.last_7_days * MILLISECONDS_PER_HOUR);
		return { from: resolvedFrom, to: resolvedTo };
	}

	return { from: new Date(now.getTime() - PRESET_HOURS[range.preset] * MILLISECONDS_PER_HOUR), to: now };
};

// ── filters ───────────────────────────────────────────────────────────────────

/** The longest search term the server accepts. Enforced here so a long paste fails visibly. */
export const MAX_ACTION_LOG_SEARCH_LENGTH = 200;

/**
 * Everything the filter bar can narrow by.
 *
 * `decisions` starts EMPTY and must stay that way. The question this page is opened with is
 * usually "why did nothing happen?", and defaulting to `fired` hides precisely the rows that
 * answer it.
 */
export interface ActionLogFilters {
	range: ActionLogDateRange;
	decisions: SocialActionDecision[];
	actionTypes: SocialActionType[];
	channelIds: string[];
	search: string;
}

export const EMPTY_ACTION_LOG_FILTERS: ActionLogFilters = {
	range: DEFAULT_ACTION_LOG_RANGE,
	decisions: [],
	actionTypes: [],
	channelIds: [],
	search: ''
};

/** Whether the window is anything other than the default the server would have chosen anyway. */
export const isCustomRange = (range: ActionLogDateRange): boolean =>
	range.preset !== DEFAULT_ACTION_LOG_RANGE.preset;

/**
 * How many axes are narrowing the list.
 *
 * Counted per axis, not per ticked box: "3" next to Decision would read as three decisions, while
 * the number on the bar as a whole is about how much of the log is hidden.
 */
export const activeActionLogFilterCount = (filters: ActionLogFilters): number =>
	(isCustomRange(filters.range) ? 1 : 0) +
	(filters.decisions.length > 0 ? 1 : 0) +
	(filters.actionTypes.length > 0 ? 1 : 0) +
	(filters.channelIds.length > 0 ? 1 : 0) +
	(filters.search.trim().length > 0 ? 1 : 0);

export const hasNarrowedActionLog = (filters: ActionLogFilters): boolean =>
	activeActionLogFilterCount(filters) > 0;

/** Label for the decision filter, so the export dialog and the bar cannot describe it differently. */
export const describeDecisionFilter = (decisions: SocialActionDecision[]): string =>
	decisions.length === 0
		? 'All decisions'
		: decisions.map((decision) => DECISION_LABELS[decision]).join(', ');

export const describeActionFilter = (actionTypes: SocialActionType[]): string =>
	actionTypes.length === 0 ? 'All actions' : actionTypes.map((actionType) => SOCIAL_ACTION_LABELS[actionType]).join(', ');
